/**
 * Conversation detail page — info cards, members table, message stats.
 * Accessed via /content/conversations/:id.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  type ConversationDetail,
  fetchConversationDetail,
} from "../../api/admin-conversations";
import { Td, Th, tableStyles } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";

export function ConversationDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, error, loading } = useConvDetail(id ?? "");

  if (loading) {
    return <p style={shared.loadingText}>{t("common.loading")}</p>;
  }
  if (error) {
    return <p style={shared.errorBanner}>{error}</p>;
  }
  if (!data) {
    return <p style={shared.loadingText}>{t("conv_detail.not_found")}</p>;
  }

  return (
    <div>
      <PageHeader
        title={t("conv_detail.title")}
        actions={
          <BackButton onClick={() => navigate("/content/conversations")} />
        }
      />
      <InfoCards detail={data} />
      <MembersSection members={data.members} />
      <StatsSection stats={data.message_stats} />
    </div>
  );
}

// ─── Back Button ───

function BackButton(props: {
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button type="button" onClick={props.onClick} style={s.backBtn}>
      {t("common.previous")}
    </button>
  );
}

// ─── Info Cards ───

function InfoCards(props: {
  detail: ConversationDetail;
}): React.JSX.Element {
  const c = props.detail.conversation;
  return (
    <section style={shared.section}>
      <h3 style={shared.sectionTitle}>{t("conv_detail.info")}</h3>
      <div style={shared.cardGrid}>
        <InfoCard
          label={t("conversations.col_type")}
          value={c.type.toUpperCase()}
        />
        <InfoCard
          label={t("conversations.col_frozen")}
          value={c.frozen ? t("common.yes") : t("common.no")}
        />
        <InfoCard
          label={t("conversations.col_terminated")}
          value={c.terminated ? t("common.yes") : t("common.no")}
        />
        <InfoCard
          label={t("conversations.col_created")}
          value={formatDate(c.created_at)}
        />
        <InfoCard
          label={t("conversations.col_last_activity")}
          value={c.last_message_at ? formatDate(c.last_message_at) : "-"}
        />
      </div>
    </section>
  );
}

function InfoCard(props: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <div style={{ ...shared.card, padding: 16 }}>
      <span style={shared.cardLabel}>{props.label}</span>
      <span style={s.cardValue}>{props.value}</span>
    </div>
  );
}

// ─── Members Section ───

function MembersSection(props: {
  members: ConversationDetail["members"];
}): React.JSX.Element {
  return (
    <section style={shared.section}>
      <h3 style={shared.sectionTitle}>{t("conv_detail.members")}</h3>
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <Th>{t("conv_detail.col_member")}</Th>
            <Th>{t("conv_detail.col_type")}</Th>
            <Th>{t("conv_detail.col_unread")}</Th>
            <Th>{t("conv_detail.col_muted")}</Th>
            <Th>{t("conv_detail.col_pinned")}</Th>
            <Th>{t("conv_detail.col_hidden")}</Th>
            <Th>{t("conv_detail.col_joined")}</Th>
          </tr>
        </thead>
        <tbody>
          {props.members.map((m) => (
            <tr key={`${m.display_name}-${m.joined_at}`}>
              <Td>{m.display_name}</Td>
              <Td>{m.member_type}</Td>
              <Td mono>{m.unread_count}</Td>
              <Td>{m.muted ? t("common.yes") : t("common.no")}</Td>
              <Td>{m.pinned ? t("common.yes") : t("common.no")}</Td>
              <Td>{m.hidden ? t("common.yes") : t("common.no")}</Td>
              <Td>{formatDate(m.joined_at)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// ─── Stats Section ───

function StatsSection(props: {
  stats: ConversationDetail["message_stats"];
}): React.JSX.Element {
  const { stats } = props;
  return (
    <section style={shared.section}>
      <h3 style={shared.sectionTitle}>{t("conv_detail.message_stats")}</h3>
      <div style={shared.cardGrid}>
        <InfoCard
          label={t("conv_detail.total_messages")}
          value={stats.total.toLocaleString()}
        />
        <InfoCard
          label={t("conv_detail.recalled")}
          value={stats.recalled.toLocaleString()}
        />
      </div>
      <TypeDistribution items={stats.by_type} />
      <SenderDistribution items={stats.by_sender} />
    </section>
  );
}

function TypeDistribution(props: {
  items: ConversationDetail["message_stats"]["by_type"];
}): React.JSX.Element {
  if (props.items.length === 0) return <></>;
  return (
    <div style={{ marginTop: 16 }}>
      <h4 style={s.subTitle}>{t("conv_detail.by_type")}</h4>
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <Th>{t("messages_tab.col_content_type")}</Th>
            <Th>{t("messages_tab.col_count")}</Th>
          </tr>
        </thead>
        <tbody>
          {props.items.map((item) => (
            <tr key={item.content_type}>
              <Td>{item.content_type}</Td>
              <Td mono>{item.count.toLocaleString()}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SenderDistribution(props: {
  items: ConversationDetail["message_stats"]["by_sender"];
}): React.JSX.Element {
  if (props.items.length === 0) return <></>;
  return (
    <div style={{ marginTop: 16 }}>
      <h4 style={s.subTitle}>{t("conv_detail.by_sender")}</h4>
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <Th>{t("conv_detail.col_type")}</Th>
            <Th>{t("messages_tab.col_count")}</Th>
          </tr>
        </thead>
        <tbody>
          {props.items.map((item) => (
            <tr key={item.sender_type}>
              <Td>{item.sender_type}</Td>
              <Td mono>{item.count.toLocaleString()}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Hook ───

function useConvDetail(id: string): {
  data: ConversationDetail | null;
  error: string | null;
  loading: boolean;
} {
  const [data, setData] = useState<ConversationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setData(await fetchConversationDetail(id));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, error, loading };
}

// ─── Helpers ───

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  backBtn: {
    padding: "6px 14px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
  },
  cardValue: {
    display: "block",
    fontSize: 18,
    fontWeight: 600,
    marginTop: 4,
  },
  subTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 8,
  },
};
